import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import { _, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { DatepickerDateFormat } from '@app/core/enums/date-format.enum';
import { SoilComposition } from '@app/core/enums/soil-composition.enum';
import { NonLeakNoiseSource } from '@app/core/enums/non-leak-noise-source.enum';
import { PressureUnit } from '@app/shared/units/pressure/pressure-unit.enum';
import { pressureUnitLocalize } from '@app/shared/units/pressure/pressure-unit.localize';
import { flowUnitLocalize } from '@app/shared/units/flow/flow-unit.localize';
import { enumToArray } from '@app/shared/utils/enumerator';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import { LdEventRepairStatus } from '@app/main/leak-detection/enums/ld-event-repair-status.enum';
import { isLeakFoundResult } from '@app/main/leak-detection/utils/ld-event-status';
import { ldEventRepairStatusLocalize } from '@app/main/leak-detection/localize/ld-event-status';
import { ldEventRepairStatusIcon } from '@app/main/leak-detection/configs/ld-event-status.config';
import {
  dialogEventRepairBorderTheme,
  dialogEventRepairChipTheme,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-badge.config';
import {
  nonLeakNoiseSourceLocalize,
  soilCompositionLocalize,
} from '@app/main/leak-detection/dialogs/dialog-event/localize/dialog-event-fields';
import { DIALOG_EVENT_OVERLAY_OPTIONS } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-overlay.config';
import { DialogEventResolutionForm } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-resolution-form.interface';
import { isRepairDateInRange } from '@app/main/leak-detection/dialogs/dialog-event/validators/dialog-event-repair-date.validator';

interface ISelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'gc-dialog-event-resolution-fields',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateDirective,
    TranslatePipe,
    Select,
    InputText,
    DatePicker,
    Button,
    SvgIconComponent,
  ],
  templateUrl: './dialog-event-resolution-fields.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class DialogEventResolutionFieldsComponent {
  private readonly translateService = inject(TranslateService);

  public form = input.required<DialogEventResolutionForm>();
  public result = input.required<LdEventInvestigationResult>();

  protected readonly overlayOptions = DIALOG_EVENT_OVERLAY_OPTIONS;
  protected readonly dateFormat = DatepickerDateFormat.DDMMYYYY;

  protected readonly flowUnitLabels = flowUnitLocalize;
  protected readonly NoiseSource = NonLeakNoiseSource;
  protected readonly RepairStatus = LdEventRepairStatus;

  protected readonly repairStatuses = enumToArray(LdEventRepairStatus);
  protected readonly repairStatusLabels = ldEventRepairStatusLocalize;
  protected readonly repairStatusIcons = ldEventRepairStatusIcon;
  protected readonly repairStatusTheme: Record<LdEventRepairStatus, string> = dialogEventRepairChipTheme;
  protected readonly repairStatusBorder: Record<LdEventRepairStatus, string> = dialogEventRepairBorderTheme;

  protected readonly today = dayjs().toDate();

  protected readonly isLeak = computed(() => this.result() !== LdEventInvestigationResult.NoLeak);

  protected readonly hasRepair = computed(() => isLeakFoundResult(this.result()));

  protected readonly pressureUnits: ISelectOption<PressureUnit>[] = enumToArray(PressureUnit).map(value => ({
    label: this.translateService.instant(pressureUnitLocalize[value]),
    value,
  }));

  protected readonly soilOptions: ISelectOption<SoilComposition>[] = enumToArray(SoilComposition).map(value => ({
    label: this.translateService.instant(soilCompositionLocalize[value]),
    value,
  }));

  protected readonly nonLeakOptions: ISelectOption<NonLeakNoiseSource>[] = enumToArray(NonLeakNoiseSource).map(
    value => ({
      label: this.translateService.instant(nonLeakNoiseSourceLocalize[value]),
      value,
    })
  );

  protected hasError(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  /** what is wrong with the repair date — out of range or unreadable, before it is merely missing */
  protected repairDateError(): string {
    const { repairDate, repairStatus } = this.form().controls;
    const errors = repairDate.errors ?? {};

    if (errors['pastDate']) return _(`@@fp-event-validation__scheduled-date-past`);
    if (errors['futureDate']) return _(`@@fp-event-validation__repair-date-future`);
    if (errors['dateFormat']) return _(`@@fp-event-validation__date-format`);

    return repairStatus.value === LdEventRepairStatus.RepairScheduled
      ? _(`@@fp-event-validation__scheduled-date`)
      : _(`@@fp-event-validation__repair-date`);
  }

  protected setRepairStatus(repairStatus: LdEventRepairStatus): void {
    const { controls } = this.form();

    controls.repairStatus.setValue(repairStatus);
    controls.repairStatus.markAsDirty();

    if (repairStatus === LdEventRepairStatus.NotRepaired) {
      controls.repairDate.setValue(null);
      controls.repairDate.disable();

      return;
    }

    controls.repairDate.setValue(keepRepairDate(controls.repairDate.value, repairStatus));
    controls.repairDate.enable();
  }
}

function keepRepairDate(date: unknown, repairStatus: LdEventRepairStatus): Date | null {
  return date instanceof Date && isRepairDateInRange(date, repairStatus) ? date : null;
}
